import React, { useState, useEffect } from 'react';

import {
  Button,
  Modal,
  Form,
  Input,
  Tooltip,
  notification,
  Checkbox,
  Spin,
} from 'antd';
import {
  updateUser,
  addMember,
  removeGroupMember,
  updateProfile,
  getProfileByUserId,
  addProfile,
} from 'src/api/api.ts';
import { EditFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import getEntitlementGroups from 'src/utils/getEntitlementGroups';
import { useAppSelector } from 'src/redux/hooks';
import { UserListMember } from '.';
import { themeSettings } from 'src/configs';

interface ModalComponentProps {
  onUserUpdated: (email: string, response: UserListMember) => void;
  userData: UserListMember;
  disabled: boolean;
}

interface UpdateUserFormType {
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  zip_code?: string;
  city?: string;
  bank_account?: string;
  isTenantAdmin: boolean;
}

const UpdateUser: React.FC<ModalComponentProps> = ({
  onUserUpdated,
  userData,
  disabled,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userIsTenantAdmin, setUserIsTenantAdmin] = useState<
    boolean | undefined
  >(undefined);
  const [profile, setProfile] = useState<API.Profile | null>(null);
  const [form] = Form.useForm();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const tenantId = useAppSelector((state) => state.user.tenant.id);
  const isRoot = useAppSelector((state) => state.user.isRoot);
  const { t } = useTranslation();
  // Helper function for handling API errors
  const handleApiError = (error: any, form: any) => {
    if (error?.response?.status === 400 && error?.response?.data) {
      const errData = error.response.data;
      if (errData.first_name) {
        form.setFields([
          { name: 'first_name', errors: [`${errData.first_name}`] },
        ]);
      }
      if (errData.last_name) {
        form.setFields([
          { name: 'last_name', errors: [`${errData.last_name}`] },
        ]);
      }
    } else {
      notification['error']({
        message: t('Error'),
        description: t('Failed to update user information, please try again.'),
        placement: 'topRight',
        duration: 4.5,
      });
    }
  };

  // Handle group membership changes
  const handleGroupMembership = async (
    isTenantAdmin: boolean,
    tenantAdminGroup: string,
    groupPayload: API.GroupMemberPayload,
    accessToken: string
  ) => {
    if (isTenantAdmin) {
      try {
        await addMember(tenantAdminGroup, groupPayload, accessToken);
      } catch (error) {
        console.error(
          `Error adding member to group ${tenantAdminGroup}:`,
          error
        );
      }
    } else {
      // Remove from tenant admin groups
      try {
        await removeGroupMember(
          tenantAdminGroup,
          groupPayload.email,
          accessToken
        );
      } catch (error) {
        console.error(
          `Error removing member from group ${tenantAdminGroup}:`,
          error
        );
      }
    }
  };

  // Main function for handling form submission
  const handleSubmit = async (values: UpdateUserFormType) => {
    setConfirmLoading(true);
    try {
      const userInfoPayload = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: userData.email,
        tenant: userData.tenant,
      };

      const response = await updateUser(
        accessToken,
        userData.email,
        userInfoPayload
      );

      // Create initial profile structure for fetchOrAddUserProfile
      const profilePayload: API.Profile = {
        id: profile?.id,
        user_id: userData.email,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone ? parseInt(values.phone) : undefined,
        address: values.address ?? '',
        zip_code: values.zip_code ?? '',
        city: values.city ?? '',
        active: true,
        bank_account: profile?.bank_account ?? '',
        admins: profile?.admins || [],
        users: profile?.users || [],
      };

      // Only include bank_account if it has changed and is not a masked value
      const formBankAccount = values.bank_account ?? '';
      const fetchedBankAccount = profilePayload.bank_account ?? '';

      if ( formBankAccount !== fetchedBankAccount) {
        // Validate bank account format (IBAN format: letters, numbers, spaces, hyphens, asterisks)
        const bankAccountRegex = /^[A-Z0-9\s-]+$/i;
        if (formBankAccount && !bankAccountRegex.test(formBankAccount)) {
          form.setFields([
            {
              name: 'bank_account',
              errors: [
                t(
                  'Invalid bank account number.'
                ),
              ],
            },
          ]);
          return;
        }
        profilePayload.bank_account = formBankAccount;
      }

      if (!profile) {
        const createdProfile = await addProfile(accessToken, profilePayload);
        profilePayload.id = createdProfile.data.id;
        profilePayload.admins = createdProfile.data.admins;
        profilePayload.users = createdProfile.data.users;
      }

      // Update user profile
      await updateProfile(accessToken, profilePayload.id!, profilePayload);

      // Handle tenant admin changes if applicable
      if (values.isTenantAdmin !== userIsTenantAdmin) {
        const { tenantAdmin } = getEntitlementGroups(tenantId);
        const groupPayload = { email: userData.email, role: 'MEMBER' };

        await handleGroupMembership(
          values.isTenantAdmin,
          tenantAdmin,
          groupPayload,
          accessToken
        );
      }

      // Success notification
      notification['success']({
        message: t('Success'),
        description: `${userData.email} : ${t('User updated successfully')}`,
        placement: 'topRight',
        duration: 4.5,
      });

      onUserUpdated(userData.email, response.data);
      setIsModalOpen(false);
    } catch (error: any) {
      error.response?.data?.detail?.forEach((error: any) => {
        console.error(error);
        form.setFields([
          {
            name: error.loc[error.loc.length - 1],
            value: form.getFieldValue(error.loc[error.loc.length - 1]),
            errors: [t(error.msg)],
          },
        ]);
      });
      handleApiError(error, form);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Helper function to get loading message
  const getLoadingMessage = () => {
    if (loading || userIsTenantAdmin === undefined)
      return t('Loading user Information');
    if (confirmLoading) return t('Updating user Information');
    return '';
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await handleSubmit(values);
    } catch (error) {
      // Form validation failed - errors are already displayed by the form
      console.log('Validation failed:', error);
    }
  };

  const fetchEntitlementsOfUser = async () => {
    try {
      setUserIsTenantAdmin(userData.isTenantAdmin);
      form.setFieldsValue({ isTenantAdmin: userData.isTenantAdmin });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await getProfileByUserId(accessToken, userData.email);
      setProfile(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.error('Profile does not exist:');
      } else {
        // Log non-404 errors
        console.error('Error fetching profile:', error);
        throw error;
      }
      setProfile(null);
    }
  };

  const loadUserData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchEntitlementsOfUser(), fetchUserProfile()]);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;
    // Reset states when modal opens
    setUserIsTenantAdmin(undefined);
    setProfile(null);
    form.resetFields();
    loadUserData();
  }, [isModalOpen]);

  useEffect(() => {
    if (!profile) return;
    form.setFieldsValue({
      first_name: userData.first_name,
      last_name: userData.last_name,
      phone: profile.phone ? profile.phone.toString() : '',
      address: profile.address || '',
      zip_code: profile.zip_code || '',
      city: profile.city || '',
      bank_account: profile.bank_account || '',
      isTenantAdmin: userIsTenantAdmin,
    });
  }, [profile, userIsTenantAdmin]);

  return (
    <>
      <Tooltip
        title={
          disabled
            ? t('You are not allowed to edit the user')
            : t('Edit this user')
        }
        color={themeSettings.token.colorPrimary}
        key={'edituser'}
      >
        <Button
          type="text"
          onClick={() => setIsModalOpen(true)}
          disabled={disabled}
        >
          <EditFilled />
        </Button>
      </Tooltip>

      <Modal
        title={t('Update Information')}
        open={isModalOpen}
        onOk={() => handleOk()}
        onCancel={() => {
          setIsModalOpen(false);
          setLoading(false);
          setUserIsTenantAdmin(undefined);
          setProfile(null);
        }}
        confirmLoading={confirmLoading}
        footer={(_, { CancelBtn }) => (
          <>
            <Button onClick={() => form.resetFields()}>{t('Reset')}</Button>
            <CancelBtn />
            <Button
              type="primary"
              onClick={handleOk}
              loading={confirmLoading}
              disabled={loading || userIsTenantAdmin === undefined}
            >
              {t('OK')}
            </Button>
          </>
        )}
      >
        <Spin
          tip={getLoadingMessage()}
          spinning={
            loading || userIsTenantAdmin === undefined || confirmLoading
          }
        >
          <Form
            form={form}
            initialValues={{
              first_name: userData.first_name,
              last_name: userData.last_name,
              phone: profile?.phone ? profile.phone.toString() : '',
              address: profile?.address || '',
              zip_code: profile?.zip_code || '',
              city: profile?.city || '',
              bank_account: profile?.bank_account || '',
              isTenantAdmin: userIsTenantAdmin,
            }}
          >
            <Form.Item
              label={t('First Name')}
              name="first_name"
              rules={[
                {
                  required: true,
                  message: t('Please enter the field!', {
                    field: t('First Name').toLocaleLowerCase(),
                  }),
                },
                {
                  pattern: new RegExp(/^[\p{L}øæåØÆÅ_ -]+$/u),
                  message: t(
                    'Only letters, spaces, hyphens and underscores are allowed'
                  ),
                },
              ]}
            >
              <Input
                placeholder={t('Enter first name here.')}
                maxLength={150}
              />
            </Form.Item>

            <Form.Item
              label={t('Last Name')}
              name="last_name"
              rules={[
                {
                  required: true,
                  message: t('Please enter the field!', {
                    field: t('Last Name').toLocaleLowerCase(),
                  }),
                },
                {
                  pattern: new RegExp(/^[\p{L}øæåØÆÅ_ -]+$/u),
                  message: t(
                    'Only letters, spaces, hyphens and underscores are allowed'
                  ),
                },
              ]}
            >
              <Input placeholder={t('Enter last name here.')} maxLength={150} />
            </Form.Item>
            <Form.Item
              label={t('Telephone')}
              name="phone"
              rules={[
                {
                  pattern: new RegExp(
                    /^\+?\d{1,3}?\s?\d{1,4}\s?\d{1,4}\s?\d{1,4}$/
                  ),
                  message: t('Please enter valid telephone no'),
                },
              ]}
            >
              <Input
                maxLength={15}
                size="middle"
                placeholder={t('Enter your telephone no. here')}
              />
            </Form.Item>
            <Form.Item label={t('Address')} name="address">
              <Input
                size="middle"
                maxLength={255}
                placeholder={t('Enter your address here')}
              />
            </Form.Item>
            <Form.Item
              label={t('Zip Code')}
              name="zip_code"
              rules={[
                {
                  pattern: new RegExp(/^\d{4}$/),
                  message: t('Zip code should have only 4 digits'),
                },
              ]}
            >
              <Input
                size="middle"
                showCount
                maxLength={4}
                placeholder={t('Enter your zip code here')}
              />
            </Form.Item>
            <Form.Item
              label={t('City')}
              name="city"
              rules={[
                {
                  pattern: new RegExp(/^[0-9a-zA-ZøæåØÆÅ#,._ -]+$/i),
                  message: t('Special characters are not allowed'),
                },
              ]}
            >
              <Input
                size="middle"
                maxLength={50}
                placeholder={t('Enter your city here')}
              />
            </Form.Item>
            <Form.Item label={t('Bank Account')} name="bank_account">
              <Input
                size="middle"
                maxLength={15}
                placeholder={t('Enter your bank account here')}
              />
            </Form.Item>
            {isRoot && (
              <Form.Item name="isTenantAdmin" valuePropName="checked">
                <Checkbox>{t('Is tenant admin?')}</Checkbox>
              </Form.Item>
            )}
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default UpdateUser;
